import { inject, Injectable } from '@angular/core';
import type {
	ActivatedRouteSnapshot,
	RouterStateSnapshot,
	UrlTree,
} from '@angular/router';
import { Router } from '@angular/router';

/**
 * URL Cleanup Guard
 *
 * Prevents issues with double slashes in URLs that can cause incorrect API calls.
 * This guard intercepts navigation attempts and cleans up malformed URLs before
 * they can cause problems with HTTP requests.
 *
 * @example
 * Problem: URL like `https://valkdeals.nl//foo/bar` causes API calls to `https://foo/bar`
 * Solution: Guard redirects to cleaned URL `https://valkdeals.nl/foo/bar`
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: '**',
 *   component: SomeComponent,
 *   canActivate: [UrlCleanupGuard]
 * }
 * ```
 *
 * @implements {CanActivate}
 */
@Injectable({
	providedIn: 'root',
})
export class UrlCleanupGuard {
	private readonly router = inject(Router);

	canActivate(
		next: ActivatedRouteSnapshot,
		routerState: RouterStateSnapshot,
	): boolean | UrlTree {
		const currentUrl = routerState.url;

		if (/\/\/+/.test(currentUrl)) {
			const cleanedUrl = currentUrl.replace(/\/\/+/g, '/');
			return this.router.parseUrl(cleanedUrl);
		}

		return true;
	}
}
